import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useWebshopStore } from '../../stores';
import type { WebshopOrder } from '../../lib/salesforce';

interface CancelWebshopOrderDialogProps {
  order: WebshopOrder | null;
  isOpen: boolean;
  onClose: () => void;
}

function formatEuro(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

export function CancelWebshopOrderDialog({ order, isOpen, onClose }: CancelWebshopOrderDialogProps) {
  const cancelOrder = useWebshopStore((s) => s.cancelOrder);
  const [reden, setReden] = useState('');
  const [refund, setRefund] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset bij heropenen / andere order
  useEffect(() => {
    if (isOpen && order) {
      setReden('');
      setRefund(Boolean(order.molliePaymentId));
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen, order]);

  if (!isOpen || !order) return null;

  const hasMolliePayment = Boolean(order.molliePaymentId);

  const handleSubmit = async () => {
    if (!reden.trim()) {
      setError('Reden is verplicht');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await cancelOrder(order.id, { reden: reden.trim(), refund });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Annuleren mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Bestelling annuleren</h2>
          <p className="text-xs text-slate-500 mt-1">
            {order.name} · {order.customerName} · {formatEuro(order.amount)}
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label className="block">
            <span className="text-sm text-slate-700">Reden <span className="text-red-500">*</span></span>
            <textarea
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              rows={3}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              placeholder="Korte reden, bijv. testorder of klant heeft per ongeluk besteld"
              disabled={submitting}
              autoFocus
            />
          </label>

          {hasMolliePayment ? (
            <label className="flex items-start gap-2 cursor-pointer select-none py-1">
              <input
                type="checkbox"
                checked={refund}
                onChange={(e) => setRefund(e.target.checked)}
                disabled={submitting}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-slate-700">
                Ook terugbetalen via Mollie ({formatEuro(order.amount)})
              </span>
            </label>
          ) : (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              Geen Mollie-betaling gekoppeld. Status wordt op <strong>Geannuleerd</strong> gezet zonder refund.
            </div>
          )}

          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {refund && hasMolliePayment
              ? 'Order wordt op Terugbetaald gezet en het bedrag wordt direct via Mollie teruggestort.'
              : 'Order wordt op Geannuleerd gezet. Niet ongedaan te maken.'}
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !reden.trim()}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !reden.trim()
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            )}
          >
            {submitting ? 'Bezig…' : 'Bevestig annulering'}
          </button>
        </div>
      </div>
    </div>
  );
}
